/**
 * DefaultCurrencyDialog Component
 * 
 * Dialog for selecting default currency.
 */

import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Alert,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COMMON_CURRENCIES } from "../hooks/useDefaultCurrency";

export interface DefaultCurrencyDialogProps {
  open: boolean;
  onClose: () => void;
  onSetDefault: (currencyCode: string) => Promise<void>;
  selectedDefaultCurrency: string;
  setSelectedDefaultCurrency: (value: string) => void;
  currentDefaultCurrency: string;
}

/**
 * Dialog for selecting default currency
 */
export default function DefaultCurrencyDialog({
  open,
  onClose,
  onSetDefault,
  selectedDefaultCurrency,
  setSelectedDefaultCurrency,
  currentDefaultCurrency,
}: DefaultCurrencyDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: "center", position: "relative" }}>
        <Typography variant="h6">Edit Default Currency</Typography>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ 
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ textAlign: "center" }}>
            Changing the default currency will update all currency rates and statistics. 
            Previously saved rates will be restored if available.
          </Alert>
          <Autocomplete
            fullWidth
            options={COMMON_CURRENCIES.map((c) => c.code)}
            value={selectedDefaultCurrency}
            onChange={(_, newValue) => {
              if (newValue) {
                setSelectedDefaultCurrency(newValue);
              }
            }}
            getOptionLabel={(option) => {
              const currency = COMMON_CURRENCIES.find((c) => c.code === option);
              return currency ? `${option} - ${currency.name} (${currency.symbol})` : option;
            }}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter((option) => {
                const currency = COMMON_CURRENCIES.find((c) => c.code === option);
                return currency
                  ? currency.code.toLowerCase().includes(searchValue) ||
                      currency.name.toLowerCase().includes(searchValue) ||
                      currency.symbol.toLowerCase().includes(searchValue)
                  : false;
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Default Currency"
                placeholder="Search and choose a currency"
                required
              />
            )}
            ListboxProps={{
              onWheel: (e) => {
                const listbox = e.currentTarget;
                if (listbox.scrollHeight <= listbox.clientHeight) return;
                e.stopPropagation();
                e.preventDefault();
                listbox.scrollTop += e.deltaY;
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSetDefault(selectedDefaultCurrency)}
          disabled={!selectedDefaultCurrency || selectedDefaultCurrency === currentDefaultCurrency}
          sx={{ flex: 1, py: 1.5 }}
        >
          Set as Default
        </Button>
      </DialogActions>
    </Dialog>
  );
}
